import * as Sentry from '@sentry/react';
import { useRoutes } from 'react-router-dom';

const environment = import.meta.env.MODE || 'development';
const dsn = import.meta.env.VITE_SENTRY_DSN;
const shouldInitialize = environment !== 'development' && !!dsn;

export const initSentry = () => {
  if (!dsn) {
    if (environment === 'development') {
      console.warn('Sentry DSN not configured. Error tracking disabled.');
    }
    return;
  }

  if (!shouldInitialize) {
    console.info('Sentry disabled in development environment.');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release: `backoffice@${__APP_VERSION__}+${__APP_BUILD_SHA__}`,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1,
    // Session Replay
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 1,
    replaysOnErrorSampleRate: 1,
  });
};

export const shouldUseSentry = (): boolean => shouldInitialize;

const stripQueryString = (url: string): string => url.split('?')[0];

const MAX_REPORTED_BODY_LENGTH = 2000;

const truncateBody = (body: unknown): unknown =>
  typeof body === 'string' && body.length > MAX_REPORTED_BODY_LENGTH
    ? `${body.slice(0, MAX_REPORTED_BODY_LENGTH)}…`
    : body;

export const sentryUseRoutes = shouldUseSentry() ? Sentry.wrapUseRoutesV7(useRoutes) : useRoutes;

interface SentryUser {
  id: number;
  email: string;
  username: string;
}

export const identifyUser = (user: SentryUser) => {
  if (!shouldUseSentry()) return;
  Sentry.setUser({ id: String(user.id), email: user.email, username: user.username });
};

export const clearUser = () => {
  if (!shouldUseSentry()) return;
  Sentry.setUser(null);
};

export const reportError = (error: unknown, tags?: Record<string, string>) => {
  if (!shouldUseSentry()) return;
  Sentry.captureException(error, tags ? { tags } : undefined);
};

export const reportWarning = (message: string, tags?: Record<string, string>) => {
  if (!shouldUseSentry()) return;
  Sentry.withScope((scope) => {
    if (tags) scope.setTags(tags);
    Sentry.captureMessage(message, 'warning');
  });
};

export const addHttpBreadcrumb = (method: string, path: string) => {
  if (!shouldUseSentry()) return;
  Sentry.addBreadcrumb({
    category: 'http',
    message: `${method} ${stripQueryString(path)}`,
    level: 'info',
  });
};

export const reportReactError = (error: unknown, componentStack: string) => {
  if (!shouldUseSentry()) return;
  Sentry.withScope((scope) => {
    scope.setContext('react', { componentStack });
    Sentry.captureException(error);
  });
};

export const reportApiError = (res: Response, body: unknown) => {
  if (!shouldUseSentry()) return;
  const url = typeof res.url === 'string' ? stripQueryString(res.url) : 'unknown';
  const level = res.status >= 500 ? 'error' : 'warning';
  Sentry.withScope((scope) => {
    scope.setTag('api.status', String(res.status));
    scope.setContext('api_response', {
      status: res.status,
      url,
      body: truncateBody(body),
    });
    scope.setLevel(level);
    Sentry.captureMessage(`HTTP ${res.status} — ${url}`, level);
  });
};

export default Sentry;
